import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import DOMPurify from 'dompurify';
import useQuoteMessaging from '../hooks/useQuoteMessaging';
import FileUpload from './FileUpload';
import './QuoteMessaging.css';

/**
 * QuoteMessaging Component
 * Displays and manages messages for quote requests or quote lines
 *
 * @param {Object} props - Component props
 * @param {number} [props.requestId] - Quote request ID
 * @param {number} [props.lineId] - Quote line ID
 * @param {boolean} [props.autoLoad] - Automatically load messages on mount
 * @param {string} [props.className] - Additional CSS classes
 */
const QuoteMessaging = forwardRef(({
  requestId,
  lineId,
  autoLoad = true,
  className = '',
}, ref) => {
  const {
    messages,
    loading,
    error,
    pagination,
    attachments,
    postRequestMessage,
    fetchRequestMessages,
    postLineMessage,
    fetchLineMessages,
    fetchRequestAttachments,
    fetchLineAttachments,
    removeAttachment,
  } = useQuoteMessaging({ requestId, lineId });

  const [messageBody, setMessageBody] = useState('');
  const [isNote, setIsNote] = useState(false);
  const [onlyNotes, setOnlyNotes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showAttachments, setShowAttachments] = useState(false);

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refresh: () => {
      console.log('🔄 Refreshing messages...');
      if (lineId) {
        fetchLineMessages({ only_notes: onlyNotes });
        fetchLineAttachments();
      } else if (requestId) {
        fetchRequestMessages({ only_notes: onlyNotes });
        fetchRequestAttachments();
      }
    }
  }), [lineId, requestId, onlyNotes, fetchLineMessages, fetchRequestMessages, fetchLineAttachments, fetchRequestAttachments]);

  // Load messages and attachments on mount
  useEffect(() => {
    if (autoLoad) {
      if (lineId) {
        fetchLineMessages({ only_notes: onlyNotes });
        fetchLineAttachments();
      } else if (requestId) {
        fetchRequestMessages({ only_notes: onlyNotes });
        fetchRequestAttachments();
      }
    }
  }, [
    autoLoad,
    lineId,
    requestId,
    onlyNotes,
    fetchLineMessages,
    fetchRequestMessages,
    fetchRequestAttachments,
    fetchLineAttachments,
  ]);

  // Handle message submission
  const handleSubmitMessage = async (e) => {
    e.preventDefault();

    if (!messageBody.trim()) {
      return;
    }

    setIsSubmitting(true);
    const hasFiles = selectedFiles && selectedFiles.length > 0;

    try {
      const messageData = {
        body: messageBody, // Send plain text, let backend handle HTML
        is_note: isNote,
        message_type: 'comment',
        files: selectedFiles, // Include selected files
      };

      if (lineId) {
        await postLineMessage(messageData);
        // Refresh messages and attachments to show uploaded files
        if (hasFiles) {
          await fetchLineMessages({ only_notes: onlyNotes });
          await fetchLineAttachments();
        }
      } else if (requestId) {
        await postRequestMessage(messageData);
        // Refresh messages and attachments to show uploaded files
        if (hasFiles) {
          await fetchRequestMessages({ only_notes: onlyNotes });
          await fetchRequestAttachments();
        }
      }

      setMessageBody('');
      setIsNote(false);
      setSelectedFiles([]); // Clear selected files
    } catch (err) {
      console.error('Failed to post message:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle file selection
  const handleFilesSelected = (files) => {
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  // Handle file removal
  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle attachment deletion
  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await removeAttachment(attachmentId);
    } catch (err) {
      console.error('Failed to delete attachment:', err);
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const filterNotes = e.target.checked;
    setOnlyNotes(filterNotes);

    if (lineId) {
      fetchLineMessages({ only_notes: filterNotes });
    } else if (requestId) {
      fetchRequestMessages({ only_notes: filterNotes });
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className={`quote-messaging ${className}`}>
      {/* Message Input Form */}
      <div className="message-input-section">
        <h3>Add Message</h3>
        <form onSubmit={handleSubmitMessage}>
          <textarea
            className="message-textarea"
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            placeholder="Type your message here..."
            rows={4}
            disabled={isSubmitting}
          />

          {/* File Upload */}
          <FileUpload
            onFilesSelected={handleFilesSelected}
            files={selectedFiles}
            onRemoveFile={handleRemoveFile}
            multiple={true}
            maxSize={10 * 1024 * 1024} // 10MB
          />

          <div className="message-controls">
            <label className="note-checkbox">
              <input
                type="checkbox"
                checked={isNote}
                onChange={(e) => setIsNote(e.target.checked)}
                disabled={isSubmitting}
              />
              <span>Internal Note</span>
            </label>
            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting || !messageBody.trim()}
            >
              {isSubmitting ? 'Posting...' : 'Post Message'}
            </button>
          </div>
        </form>
      </div>

      {/* Messages List */}
      <div className="messages-section">
        <div className="messages-header">
          <h3>Messages ({pagination.total})</h3>
          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={onlyNotes}
              onChange={handleFilterChange}
              disabled={loading}
            />
            <span>Only Notes</span>
          </label>
        </div>

        {error && <div className="error-message">Error: {error}</div>}

        {loading && <div className="loading-indicator">Loading messages...</div>}

        {!loading && messages.length === 0 && (
          <div className="no-messages">No messages yet.</div>
        )}

        <div className="messages-list">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message-item ${message.is_note ? 'note' : 'comment'}`}
            >
              <div className="message-header">
                <span className="message-author">{message.author}</span>
                <span className="message-date">{formatDate(message.date)}</span>
                {message.is_note && <span className="note-badge">Internal Note</span>}
              </div>
              <div
                className="message-body"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.body) }}
              />
              {message.attachments && message.attachments.length > 0 && (
                <div className="message-attachments">
                  <div className="attachments-label">
                    📎 Attachments ({message.attachments.length}):
                  </div>
                  <div className="attachments-list">
                    {message.attachments.map((att) => (
                      <a
                        key={att.id}
                        href={att.url}
                        className="attachment-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                      >
                        📄 {att.name}
                        <span className="attachment-size">
                          ({Math.round(att.file_size / 1024)} KB)
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {message.subtype && message.subtype !== 'Note' && (
                <div className="message-subtype">{message.subtype}</div>
              )}
            </div>
          ))}
        </div>

        {pagination.total > pagination.limit && (
          <div className="pagination-info">
            Showing {messages.length} of {pagination.total} messages
          </div>
        )}
      </div>

      {/* Standalone Attachments Section */}
      {attachments.length > 0 && (
        <div className="attachments-section">
          <div className="attachments-section-header">
            <h3>All Attachments ({attachments.length})</h3>
            <button
              className="toggle-attachments-btn"
              onClick={() => setShowAttachments(!showAttachments)}
            >
              {showAttachments ? '▼ Hide' : '▶ Show'}
            </button>
          </div>

          {showAttachments && (
            <div className="attachments-grid">
              {attachments.map((att) => (
                <div key={att.id} className="attachment-card">
                  <div className="attachment-icon">📄</div>
                  <div className="attachment-details">
                    <a
                      href={att.url}
                      className="attachment-name"
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      {att.name}
                    </a>
                    <div className="attachment-meta">
                      <span className="attachment-size">
                        {Math.round(att.file_size / 1024)} KB
                      </span>
                      <span className="attachment-date">
                        {att.create_date ? new Date(att.create_date).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </div>
                  <button
                    className="delete-attachment-btn"
                    onClick={() => handleDeleteAttachment(att.id)}
                    title="Delete attachment"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

QuoteMessaging.displayName = 'QuoteMessaging';

export default QuoteMessaging;
